import React, { useCallback, useEffect, useState } from 'react';
import { AlarmClock, X } from 'lucide-react';
import {
  isNativeShell,
  canScheduleExactAdhan,
  openExactAlarmSettings,
} from '../utils/prayerNotifications';
import {
  shouldShowExactPrompt,
  readExactPromptDismissedAt,
  markExactPromptDismissed,
} from '../utils/exactAlarmPrompt';

/** أندرويد وحده: مُلحق الأذان لا يوجد على iOS ولا في المتصفح. */
const isNativeAndroid = () => isNativeShell() && Boolean(window.Capacitor?.Plugins?.Adhan);

/**
 * يطلب إذن «المنبّهات والتذكيرات».
 *
 * أندرويد ١٤ يمنعه افتراضياً، فيصل أكثر المستخدمين الجدد بلا أذانٍ في وقته.
 * والنظام لا يعرض نافذة طلبٍ لهذا الإذن، فلا بدّ أن نشرح ونأخذ المستخدم إليه.
 */
export function ExactAlarmPrompt() {
  const [visible, setVisible] = useState(false);

  // الحالة تُضبط بعد جواب الملحق (غير متزامن)، لا داخل المؤثّر مباشرة
  const check = useCallback(
    () =>
      canScheduleExactAdhan().then((exactAllowed) =>
        setVisible(
          shouldShowExactPrompt({
            exactAllowed,
            dismissedAt: readExactPromptDismissedAt(),
            now: Date.now(),
          }),
        ),
      ),
    [],
  );

  useEffect(() => {
    if (!isNativeAndroid()) return undefined;
    check();
    // يعود المستخدم من الإعدادات: إن منح الإذن تختفي البطاقة وحدها
    const onVisible = () => {
      if (document.visibilityState === 'visible') check();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [check]);

  if (!visible) return null;

  const later = () => {
    markExactPromptDismissed();
    setVisible(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center bg-ink/40 backdrop-blur-sm animate-fadeIn"
      onClick={later}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="exact-alarm-title"
        dir="rtl"
        className="bg-surface border border-line rounded-t-lg w-full max-w-md shadow-lift safe-bottom-edge"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-5 flex items-start gap-3">
          <div className="w-10 h-10 shrink-0 rounded-xl bg-primary/10 border border-line flex items-center justify-center text-primary">
            <AlarmClock className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 id="exact-alarm-title" className="text-lg font-semibold text-ink">
              الأذان في وقته
            </h3>
            <p className="mt-1 text-sm leading-relaxed text-ink-muted">
              بدون إذن «المنبّهات والتذكيرات» قد يتأخّر الأذان دقائق عن وقته، وقد يُرفع
              إشعاراً فقط فلا يُسمع في وضع «عدم الإزعاج».
            </p>
          </div>
          <button
            type="button"
            onClick={later}
            aria-label="إغلاق"
            className="w-8 h-8 shrink-0 rounded-lg text-ink-muted hover:text-ink flex items-center justify-center transition"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-5 flex flex-col gap-2">
          <button
            type="button"
            onClick={openExactAlarmSettings}
            className="w-full h-11 rounded-lg bg-primary text-primary-ink text-sm font-semibold transition hover:opacity-90"
          >
            تفعيل الأذان في وقته
          </button>
          <button
            type="button"
            onClick={later}
            className="w-full h-11 rounded-lg border border-line text-ink-muted text-sm font-semibold transition hover:text-ink"
          >
            لاحقاً
          </button>
        </div>
      </div>
    </div>
  );
}
